import { useState } from "react";
import { DateRangePicker } from "@/components/shared/DateRangePicker";
import { format } from "date-fns";
import { ArrowDownRight, ArrowUpRight, Package } from "lucide-react";
import {
  Area,
  Bar,
  CartesianGrid,
  ComposedChart,
  Dot,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Card } from "@/components/ui/card";
import { TruncatedText } from "@/components/ui/truncated-text";
import { cn } from "@/lib/utils";
import type {
  DailyRanksKeyword,
  DailyRanksProduct,
  DailyRanksResponse,
  DailyRanksTheme,
} from "@/types/rankTracker";
import { KpsBreakdownPopover, type KpsBreakdownViewModel } from "@/components/keyword-bank/KpsBreakdownPopover";
import { buildKeywordKpsViewModel, buildKpsRollUpViewModel } from "../kpsViewModel";
import {
  AD_SPEND_SERIES_NAME,
  AdSpendCard,
  ROLLUP_AD_SPEND_LABEL,
  ROLLUP_AD_SPEND_SERIES_NAME,
  sharedSpendLabel,
} from "./AdSpendCard";

/**
 * FR-12/FR-13/FR-15: one modal, three call sites, discriminated on `level` so no row can open
 * another row's view (a type error, not a convention) --
 * `decisions/the-rank-detail-view-is-one-modal-over-a-discriminated-target.md`. `product` is
 * present on every arm -- it is what the product photo/name/ASIN block and the units-sold/BSR
 * series read regardless of which row opened the modal.
 */
export type RankDetailTarget =
  | { level: "keyword"; product: DailyRanksProduct; theme: DailyRanksTheme; keyword: DailyRanksKeyword }
  | { level: "theme"; product: DailyRanksProduct; theme: DailyRanksTheme }
  | { level: "product"; product: DailyRanksProduct };

/**
 * Plots one row's position (a keyword's, a theme's, or one line per theme for a hero product)
 * against the hero product's real units sold and Best Sellers Rank for the same days -- all three
 * come from the daily-ranks response the table already holds. No correlation coefficient, strength
 * word or percentage (`constraints.md § C-35`). Nothing here is derived from the rank: `unitsSold`
 * and `bsr` are stored per product-day (`components/rank-tracker.md § unitsSold and bsr, and why
 * FR-12 needs no endpoint`).
 *
 * Carries its own `Last 7/30/60/90 days` picker -- the table's window is not this modal's only
 * window. Fetches the full 90 days once (in `RankTracker.tsx`, only while this modal is open) and
 * slices it client-side here, so this component gains a range control of its own rather than only
 * ever mirroring the table's.
 */
export interface KeywordRankDetailModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  target: RankDetailTarget;
  /** YYYY-MM-DD, oldest first -- the table's currently held window. Used as-is until
   * `extendedRanks` resolves (or if this target cannot be found in it), and as the ceiling on
   * which of the range picker's presets are selectable until then. */
  days: string[];
  /**
   * The full 90-day daily-ranks response, fetched by `RankTracker.tsx` only while this modal is
   * open, `undefined` until it resolves. This component slices it client-side by the selected
   * preset once it (a) has arrived and (b) still contains this exact target -- a keyword can go
   * untracked, or a theme deleted, between the table's own fetch and this one resolving, and that
   * case falls back to `days` plus the series on `target` itself, exactly as if `extendedRanks`
   * had never arrived. `metrics`/`kpsRollUp` are never read out of `extendedRanks` at all -- see
   * `buildStats` below.
   */
  extendedRanks?: DailyRanksResponse;
  /**
   * AUT-440. `DailyRanksResponse.adSpendEndDate` — the last day the ad pipeline has landed data for,
   * global to the run, not per keyword (`components/rank-tracker.md § The two gates on the wire`).
   * Passed from the table's response so the modal has it even before `extendedRanks` resolves; when
   * `extendedRanks` is present its own (identical, global) value is preferred. Keys the chart's
   * missing-data cutoff: a visible day after this date is drawn in the missing-data style, never as
   * `$0` (FR-17). `null` when the ad table holds nothing. Never parsed into a `Date` — a
   * `YYYY-MM-DD` string comparison against the day axis is chronological.
   */
  adSpendEndDate?: string | null;
}

const DASH = "–";

/** The four fixed windows this modal's own picker offers -- the same four `RankTracker.tsx`'s
 * `RankTrackerWindowPicker` offers, since 90 is the backend's hard maximum (NFR-1). */
const MODAL_WINDOW_PRESETS = [
  { days: 7, label: "Last 7 days" },
  { days: 30, label: "Last 30 days" },
  { days: 60, label: "Last 60 days" },
  { days: 90, label: "Last 90 days" },
] as const;

/** The widest preset that does not exceed what is actually available -- so the picker never opens
 * on a value labelled wider than the data it is about to render. Falls back to the narrowest
 * preset in the (real-world unreachable) case that even that exceeds `maxDays`; slicing a shorter
 * array by a wider preset is harmless -- `Array.prototype.slice` simply clamps. */
function largestAvailablePreset(maxDays: number): number {
  const eligible = MODAL_WINDOW_PRESETS.filter((preset) => preset.days <= maxDays);
  return eligible.length > 0 ? eligible[eligible.length - 1].days : MODAL_WINDOW_PRESETS[0].days;
}

/** Mirrors `RankTrackerWindowPicker` in `RankTracker.tsx` exactly -- a second, page-level
 * component of the same shape rather than an import from a page module, since a page importing
 * this modal that itself imported back from that page would be a circular dependency for no real
 * reuse (the two pickers differ in which presets can be disabled). */
function KeywordModalWindowPicker({
  selectedDays,
  onChange,
  maxAvailableDays,
}: {
  selectedDays: number;
  onChange: (days: number) => void;
  maxAvailableDays: number;
}) {
  // AUT-420: this was still a bare `<select>` after AUT-356 converted every other picker -- it was
  // missed because it imports no Calendar component, which is what that sweep searched for. Windows
  // wider than the history this keyword holds stay visible but disabled, as they were as `<option
  // disabled>`, so the option's existence is still discoverable. `allowCustom` is false: the chart
  // has no affordance for an arbitrary range and the backend caps the span at 90 days (NFR-1).
  return (
    <DateRangePicker
      selectedDays={selectedDays}
      onDaysChange={onChange}
      allowCustom={false}
      ariaLabel="Date range"
      presets={MODAL_WINDOW_PRESETS.map((preset) => ({
        key: String(preset.days),
        label: preset.label,
        days: preset.days,
        disabled: preset.days > maxAvailableDays,
      }))}
    />
  );
}

function formatCount(value: number | null): string {
  return value == null ? DASH : value.toLocaleString();
}

/** FR-14: the `-1` "pending recalculation" sentinel on `kps` is never rendered as a value. */
function formatKps(value: number | null): string {
  return value == null || value === -1 ? DASH : String(value);
}

function formatSignedInt(value: number): string {
  return value > 0 ? `+${value}` : String(value);
}

/**
 * `new Date("2026-06-11")` parses as UTC midnight and draws the previous day west of Greenwich
 * (`components/rank-tracker.md § Target state — the screen`). Constructing from the parsed
 * y/m/d in the local timezone avoids that shift.
 */
function formatCalendarDate(ymd: string): string {
  const [year, month, day] = ymd.split("-").map(Number);
  return format(new Date(year, month - 1, day), "MMM d");
}

/**
 * First known rank − last known rank; positive means the rank improved (moved closer to #1). This is
 * the user-facing convention, not raw rank arithmetic -- a lower rank number is better, so `last -
 * first` (AUT-385's bug) prints an improvement as negative. Match this sign to the other three sites
 * that already compute a rank delta: `RankChangeIndicator.tsx` (`previousRank - currentRank`),
 * `RankHeatmapTable/FilterDropdown.tsx` (`firstKnown - lastKnown`), and the backend's `avgRankDelta`
 * (`components/rank-tracker.md`). Null below two known ranks.
 */
function computeRankDelta(positions: (number | null)[]): number | null {
  const knownIndices: number[] = [];
  positions.forEach((position, index) => {
    if (position != null) knownIndices.push(index);
  });
  if (knownIndices.length < 2) return null;
  const first = positions[knownIndices[0]] as number;
  const last = positions[knownIndices[knownIndices.length - 1]] as number;
  return first - last;
}

/**
 * AUT-380: a reading is isolated when both its neighbouring days are empty -- `null`, or simply
 * absent because the reading sits at the edge of the currently selected window. Under
 * `connectNulls={false}` such a day draws as a degenerate `moveto`+`closepath` subpath, which SVG
 * does not paint under the default `stroke-linecap: butt` -- so at the product level, where dots
 * are otherwise off (see the `dot` prop below), that reading would render as nothing at all. `days`
 * is the line's own already-window-sliced `positions` array -- the same one the curve is drawn
 * from -- never `payload`, so this can never disagree with what is actually plotted.
 */
function isIsolatedReading(positions: (number | null)[], index: number): boolean {
  if (positions[index] == null) return false;
  const previousIsGap = index === 0 || positions[index - 1] == null;
  const nextIsGap = index === positions.length - 1 || positions[index + 1] == null;
  return previousIsGap && nextIsGap;
}

/** The subset of `Line`'s per-point dot-render props (recharts/lib/cartesian/Line.js
 * `renderDots`) this modal actually reads -- `index` to test isolation, `cx`/`cy` to place the
 * marker. Recharts types this callback's props as `any`; naming the shape here keeps this file's
 * own code typed even though the library does not. */
interface LineDotRenderProps {
  index?: number;
  cx?: number;
  cy?: number;
}

/** `${n} ${word}` / `${n} ${word}s` -- every pluralised count this modal renders (a roll-up
 * caption's base, a subtitle's keyword or theme count) is this one rule. */
function pluralWord(n: number, word: string): string {
  return `${n} ${word}${n === 1 ? "" : "s"}`;
}

/** Tile captions 2 and 4 at the roll-up levels: `combined total, {n} keywords`. Tile 3:
 * `average, {n} keywords`. FR-2/FR-5a: the base travels with the figure it describes. */
function rollUpCaption(prefix: "combined total" | "average", keywordCount: number): string {
  return `${prefix}, ${pluralWord(keywordCount, "keyword")}`;
}

/** One line of the chart -- a single `Keyword Rank` line at the keyword/theme levels, or one
 * per theme (`dataKey` positional, `theme{i}`) at the product level (FR-13). */
interface ChartLine {
  key: string;
  name: string;
  positions: (number | null)[];
}

interface ResolvedSeries {
  days: string[];
  unitsSold: (number | null)[];
  bsr: (number | null)[];
  lines: ChartLine[];
  /** The series tile 1's delta is computed over -- the row's own per-day series: a keyword's
   * `positions`, a theme's `positions`, or a product's own `positions` (its heat row's series,
   * not a mean of its themes' deltas or a recomputation over the per-theme lines). */
  deltaPositions: (number | null)[];
  /**
   * AUT-440 / roll-up-ad-spend. The resolved row's daily allocated ad spend -- a keyword's own, or a
   * theme's or product's roll-up with each match key counted once (gate 1: null-or-full with the
   * four fields below and `maxAsinsInAdGroup`). `null` for a row whose ASIN carried no ad row
   * anywhere in the window (FR-20), and for a payload from a backend that predates the roll-up
   * (`undefined` is read as `null`) -- the card, the `$` axis and the series then do not draw at all. Resolved from the same payload as `days` through the
   * all-or-nothing fallback, so the series is always index-aligned with the day axis.
   */
  adSpend: (number | null)[] | null;
  adClicks: (number | null)[] | null;
  adImpressions: (number | null)[] | null;
  adOrders: (number | null)[] | null;
  /** The largest number of ASINs sharing this row's ad group over the window; `> 1` makes the
   * spend a shared estimate (FR-19). `null` exactly when `adSpend` is null. */
  maxAsinsInAdGroup: number | null;
}

/**
 * The `extendedRanks` lookup, per level, and the all-or-nothing fallback
 * (`components/rank-tracker.md § The detail view at three levels`). Every level starts by finding
 * the product by `id`; a keyword call then finds its keyword by `id` across the flattened themes
 * (unchanged from what ships); a theme call finds its theme by `themeId`, including `null` --
 * unique per product because `BuildDailyRankGrid` keys themes by `themeId ?? '__no_theme__'`; a
 * product call needs no further step. If any step fails, every field here falls back to the
 * `target` object handed to this component -- never a per-field mix of the two payloads. This
 * governs `days`/`lines`/`deltaPositions`/`unitsSold`/`bsr` and nothing else: `metrics` and
 * `kpsRollUp` are never resolved here at all (see `buildStats`).
 */
function resolveSeries(
  target: RankDetailTarget,
  days: string[],
  extendedRanks?: DailyRanksResponse,
): ResolvedSeries {
  const extendedProduct = extendedRanks?.products.find((p) => p.id === target.product.id);

  if (target.level === "keyword") {
    const extendedKeyword = extendedProduct?.themes
      .flatMap((theme) => theme.keywords)
      .find((k) => k.id === target.keyword.id);
    const extended =
      extendedRanks && extendedProduct && extendedKeyword
        ? {
            days: extendedRanks.days,
            positions: extendedKeyword.positions,
            unitsSold: extendedProduct.unitsSold,
            bsr: extendedProduct.bsr,
            keyword: extendedKeyword,
          }
        : null;
    const positions = extended?.positions ?? target.keyword.positions;
    // The ad fields travel with the rest of the all-or-nothing swap: they come from the extended
    // keyword exactly when `days`/`positions` do, so `adSpend` is always index-aligned with the day
    // axis actually drawn. A keyword's own null `adSpend` (no ad rows) already carries the FR-20
    // "nothing draws" state.
    const adKeyword = extended?.keyword ?? target.keyword;
    return {
      days: extended?.days ?? days,
      unitsSold: extended?.unitsSold ?? target.product.unitsSold,
      bsr: extended?.bsr ?? target.product.bsr,
      lines: [{ key: "rank", name: "Keyword Rank", positions }],
      deltaPositions: positions,
      adSpend: adKeyword.adSpend,
      adClicks: adKeyword.adClicks,
      adImpressions: adKeyword.adImpressions,
      adOrders: adKeyword.adOrders,
      maxAsinsInAdGroup: adKeyword.maxAsinsInAdGroup,
    };
  }

  if (target.level === "theme") {
    const extendedTheme = extendedProduct?.themes.find((t) => t.themeId === target.theme.themeId);
    const extended =
      extendedRanks && extendedProduct && extendedTheme
        ? {
            days: extendedRanks.days,
            positions: extendedTheme.positions,
            unitsSold: extendedProduct.unitsSold,
            bsr: extendedProduct.bsr,
            theme: extendedTheme,
          }
        : null;
    const positions = extended?.positions ?? target.theme.positions;
    // The six roll-up ad fields travel with the all-or-nothing swap, as at keyword level.
    const adTheme = extended?.theme ?? target.theme;
    return {
      days: extended?.days ?? days,
      unitsSold: extended?.unitsSold ?? target.product.unitsSold,
      bsr: extended?.bsr ?? target.product.bsr,
      // The series keeps the name `Keyword Rank` at the theme level -- the axis already reads it,
      // and the vocabulary stays one across the keyword and theme calls. The Δ tile below shares
      // this same `Keyword rank Δ` label at all three levels (`sources.md § 4.11`'s shared-copy
      // table).
      lines: [{ key: "rank", name: "Keyword Rank", positions }],
      deltaPositions: positions,
      adSpend: adTheme.adSpend,
      adClicks: adTheme.adClicks,
      adImpressions: adTheme.adImpressions,
      adOrders: adTheme.adOrders,
      maxAsinsInAdGroup: adTheme.maxAsinsInAdGroup,
    };
  }

  // target.level === "product"
  const extended =
    extendedRanks && extendedProduct
      ? {
          days: extendedRanks.days,
          positions: extendedProduct.positions,
          unitsSold: extendedProduct.unitsSold,
          bsr: extendedProduct.bsr,
          themes: extendedProduct.themes,
          product: extendedProduct,
        }
      : null;
  // The six roll-up ad fields travel with the all-or-nothing swap, as at keyword level.
  const adProduct = extended?.product ?? target.product;
  const themes = extended?.themes ?? target.product.themes;
  return {
    days: extended?.days ?? days,
    unitsSold: extended?.unitsSold ?? target.product.unitsSold,
    bsr: extended?.bsr ?? target.product.bsr,
    // FR-13: one Line per theme, no top-N cap -- every theme is drawn.
    lines: themes.map((theme, index) => ({
      key: `theme${index}`,
      name: theme.name,
      positions: theme.positions,
    })),
    // FR-14: the product's own per-day roll-up, the same series its heat row draws -- not a mean
    // of the per-theme deltas and not a delta recomputed from the per-theme lines above.
    deltaPositions: extended?.positions ?? target.product.positions,
    // The product's roll-up: flattened over its keywords, each match key once -- not a fold over
    // the themes above (`components/rank-tracker.md § Theme and hero-product ad spend`).
    adSpend: adProduct.adSpend,
    adClicks: adProduct.adClicks,
    adImpressions: adProduct.adImpressions,
    adOrders: adProduct.adOrders,
    maxAsinsInAdGroup: adProduct.maxAsinsInAdGroup,
  };
}

interface ModalStats {
  title: string;
  subtitle: string;
  /** Tile 1's label, without its `({d}D)` suffix -- that is appended at render time from the
   * window actually drawn. */
  deltaLabel: string;
  tile2: { value: string; caption: string };
  /** `kpsViewModel` feeds the same `KpsBreakdownPopover` the row's own KPS cell renders --
   * `null` exactly when there is nothing to open (`sources.md § 4.11`'s shared-copy table puts the
   * gauge on tile 3 at all three levels). Built from `target`, never `extendedRanks` -- same rule
   * as the rest of this function, since these are window-dependent figures. */
  tile3: { value: string; caption: string; kpsViewModel: KpsBreakdownViewModel | null };
  tile4: { value: string; caption: string };
}

/**
 * Tiles 2-4 and the subtitle, read off `target` unconditionally -- never off `extendedRanks`, in
 * the fallback branch and out of it. `metrics` is window-dependent (`searchVolume` is the
 * chronologically last snapshot *in the window*), so a tile resolved from the 90-day payload would
 * print a different number from the cell in the row the user actually clicked. Tile 1 is the only
 * figure that follows the drawn window instead, computed in the render body from
 * `resolveSeries`'s `deltaPositions`.
 */
function buildStats(target: RankDetailTarget): ModalStats {
  if (target.level === "keyword") {
    return {
      title: target.keyword.keyword,
      subtitle: `${target.theme.name} · ${formatCount(target.keyword.searchVolume)} / mo search volume`,
      deltaLabel: "Keyword rank Δ",
      tile2: { value: formatCount(target.keyword.searchVolume), caption: "monthly searches" },
      tile3: {
        value: formatKps(target.keyword.kps),
        caption: "keyword priority score",
        kpsViewModel: buildKeywordKpsViewModel(target.keyword),
      },
      tile4: { value: formatCount(target.keyword.keywordSales), caption: "est. monthly sales" },
    };
  }

  if (target.level === "theme") {
    const { keywordCount, metrics } = target.theme;
    const subtitle =
      pluralWord(keywordCount, "tracked keyword") +
      (metrics.searchVolume.value != null
        ? ` · ${formatCount(metrics.searchVolume.value)} / mo, combined total of ${pluralWord(metrics.searchVolume.keywordCount, "keyword")}`
        : "");
    return {
      title: target.theme.name,
      subtitle,
      deltaLabel: "Keyword rank Δ",
      tile2: {
        value: formatCount(metrics.searchVolume.value),
        caption: rollUpCaption("combined total", metrics.searchVolume.keywordCount),
      },
      tile3: {
        value: formatKps(metrics.kps.value),
        caption: rollUpCaption("average", metrics.kps.keywordCount),
        kpsViewModel: buildKpsRollUpViewModel(target.theme.name, metrics, target.theme.kpsRollUp),
      },
      tile4: {
        value: formatCount(metrics.keywordSales.value),
        caption: rollUpCaption("combined total", metrics.keywordSales.keywordCount),
      },
    };
  }

  // target.level === "product"
  const { metrics } = target.product;
  return {
    title: target.product.name,
    subtitle: `${pluralWord(target.product.themes.length, "theme")} · ${pluralWord(target.product.keywordCount, "tracked keyword")}`,
    deltaLabel: "Keyword rank Δ",
    tile2: {
      value: formatCount(metrics.searchVolume.value),
      caption: rollUpCaption("combined total", metrics.searchVolume.keywordCount),
    },
    tile3: {
      value: formatKps(metrics.kps.value),
      caption: rollUpCaption("average", metrics.kps.keywordCount),
      kpsViewModel: buildKpsRollUpViewModel(target.product.name, metrics, target.product.kpsRollUp),
    },
    tile4: {
      value: formatCount(metrics.keywordSales.value),
      caption: rollUpCaption("combined total", metrics.keywordSales.keywordCount),
    },
  };
}

/** The mockup's `StatTile`. */
function StatTile({
  label,
  value,
  valueClassName,
  caption,
}: {
  label: string;
  value: React.ReactNode;
  valueClassName?: string;
  caption: string;
}) {
  return (
    <div className="rounded-sm bg-muted/50 p-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
      <p
        className={cn(
          "mt-1 flex items-center gap-1 text-2xl font-semibold text-foreground-strong tabular-nums",
          valueClassName,
        )}
      >
        {value}
      </p>
      <p className="mt-1 text-xs text-muted-foreground">{caption}</p>
    </div>
  );
}

/** One integer metric line in the tooltip -- thousands-separated, or the em dash when absent. */
function formatTooltipInt(value: unknown): string {
  return typeof value === "number" ? value.toLocaleString() : DASH;
}

/** The subset of a recharts tooltip payload entry this file reads. Recharts types these as a broad
 * union; naming the shape keeps this file's own code typed. `payload` is the whole chart-data row
 * for the hovered day, which is where the non-plotted ad metrics (clicks/impressions/orders) ride. */
interface ChartTooltipEntry {
  dataKey?: string | number;
  name?: string;
  value?: number | string | null;
  color?: string;
  payload?: Record<string, unknown>;
}

interface ChartTooltipProps {
  /** Injected by recharts via `cloneElement` on the `content` element. */
  active?: boolean;
  payload?: ChartTooltipEntry[];
  label?: string;
  /** `maxAsinsInAdGroup` for the hovered row (keyword, theme or product); `> 1` appends the
   * shared-spend line (FR-19). `null` where the row has no ad series. */
  maxAsinsInAdGroup?: number | null;
}

/**
 * AUT-440. The chart tooltip. It carries the plotted series (units sold, rank, BSR, and -- at the
 * keyword, theme and product levels -- allocated ad spend) as the design capture's generic
 * `name : value` list, and adds
 * the two things the prototype's tooltip lacked (`components/rank-tracker.md § The screen`):
 * - **clicks, impressions and orders for the day** (FR-18), read off the hovered row rather than
 *   from a plotted series -- they have no axis. Shown only on a day that carries spend data, so a
 *   post-`adSpendEndDate` (missing) day shows none.
 * - **the shared-spend disclosure** (FR-19) when the row's ad group spans more than one ASIN,
 *   using the same `sharedSpendLabel` string the card reads.
 * The component is level-agnostic: it prints whatever entries recharts hands it. A day whose plotted
 * spend is `null` reaches it without an `adSpend` entry (recharts filters null), so it shows none of the
 * four ad entries there, at any level.
 */
export function ChartTooltip({ active, payload, label, maxAsinsInAdGroup }: ChartTooltipProps) {
  if (!active || !payload || payload.length === 0) return null;
  const row = payload[0]?.payload;
  const dayHasSpend = row != null && row.adSpend != null;
  const isShared = maxAsinsInAdGroup != null && maxAsinsInAdGroup > 1;
  return (
    <div className="rounded-md border border-border bg-popover p-3 text-xs shadow-md">
      {label != null && <p className="mb-1 text-foreground">{label}</p>}
      {payload.map((item) => {
        const key = String(item.dataKey);
        // The bar's own fill is a 20%-alpha primary; both it and the spend area read a solid colour
        // as tooltip text (the capture's tooltip does the same). The spend row takes the prototype's
        // Sponsored-Products accent (`--heat-sp-3`, the area's own stroke). Every other row keeps its
        // mark's stroke.
        const color =
          key === "adSpend"
            ? "hsl(var(--heat-sp-3))"
            : key === "unitsSold"
              ? "hsl(var(--foreground))"
              : item.color;
        const value =
          key === "adSpend"
            ? typeof item.value === "number"
              ? `$${item.value.toFixed(2)}`
              : DASH
            : item.value == null
              ? DASH
              : typeof item.value === "number"
                ? item.value.toLocaleString()
                : String(item.value);
        return (
          <p key={key} className="py-0.5" style={{ color }}>
            {item.name} : {value}
          </p>
        );
      })}
      {dayHasSpend && (
        <>
          <p className="py-0.5 text-muted-foreground">Clicks : {formatTooltipInt(row?.adClicks)}</p>
          <p className="py-0.5 text-muted-foreground">Impressions : {formatTooltipInt(row?.adImpressions)}</p>
          <p className="py-0.5 text-muted-foreground">Orders : {formatTooltipInt(row?.adOrders)}</p>
        </>
      )}
      {isShared && (
        <p className="mt-1 border-t border-border pt-1 text-muted-foreground">
          {sharedSpendLabel(maxAsinsInAdGroup)}
        </p>
      )}
    </div>
  );
}

export function KeywordRankDetailModal({
  open,
  onOpenChange,
  target,
  days,
  extendedRanks,
  adSpendEndDate,
}: KeywordRankDetailModalProps) {
  const [selectedDays, setSelectedDays] = useState(() => largestAvailablePreset(days.length));

  const resolved = resolveSeries(target, days, extendedRanks);
  const stats = buildStats(target);

  const maxAvailableDays = resolved.days.length;
  const visibleDays = resolved.days.slice(-selectedDays);
  const visibleUnitsSold = resolved.unitsSold.slice(-selectedDays);
  const visibleBsr = resolved.bsr.slice(-selectedDays);
  const visibleLines = resolved.lines.map((line) => ({
    ...line,
    positions: line.positions.slice(-selectedDays),
  }));
  const visibleDeltaPositions = resolved.deltaPositions.slice(-selectedDays);

  // -- AUT-440 ad spend ---------------------------------------------------------------------------
  // Gate 1: the card, the `$` axis and the series exist together or not at all -- `adSpend !== null`
  // decides, at all three levels (FR-16-20). The check is loose (`!= null`) on purpose: a bundle
  // against a backend that predates the roll-up sees `undefined` and must draw four tiles.
  // `adSeriesActive` is that one condition.
  const adSeriesActive = resolved.adSpend != null;
  const adLabel = target.level === "keyword" ? "Ad spend" : ROLLUP_AD_SPEND_LABEL;
  const adSeriesName = target.level === "keyword" ? AD_SPEND_SERIES_NAME : ROLLUP_AD_SPEND_SERIES_NAME;
  // The pipeline's end date is global; prefer the sliced payload's own copy, else the table's.
  const effectiveAdSpendEndDate = extendedRanks?.adSpendEndDate ?? adSpendEndDate ?? null;
  const visibleAdSpend = resolved.adSpend?.slice(-selectedDays) ?? null;
  const visibleAdClicks = resolved.adClicks?.slice(-selectedDays) ?? null;
  const visibleAdImpressions = resolved.adImpressions?.slice(-selectedDays) ?? null;
  const visibleAdOrders = resolved.adOrders?.slice(-selectedDays) ?? null;
  // Gate 2, applied by the client per FR-17: a day at or before the end date keeps its value (a
  // number, `0` included); a day after it is forced to the missing-data state (`null`), whatever the
  // array holds -- so `$0` and "no data yet" never share a style. `YYYY-MM-DD` compares
  // chronologically as a string, so no `Date` is constructed. With no end date nothing can be
  // before it, so the whole series reads as missing.
  const plottedSpend: (number | null)[] = visibleDays.map((day, index) => {
    const value = visibleAdSpend?.[index] ?? null;
    if (value == null) return null;
    if (effectiveAdSpendEndDate == null || day > effectiveAdSpendEndDate) return null;
    return value;
  });
  // Window-scoped, the fourth divergence the ticket resolves: the card sums the days actually drawn,
  // not the response-level `adSpendTotal`. `hasWindowSpend` false (an all-`$0` or entirely-missing
  // window) is what shows `No Sponsored Products spend` rather than `$0`.
  const adWindowTotal = plottedSpend.reduce((sum, value) => sum + (value ?? 0), 0);
  const adWindowHasSpend = plottedSpend.some((value) => value != null && value > 0);

  const delta = computeRankDelta(visibleDeltaPositions);
  const deltaCaption =
    delta == null
      ? "not enough data"
      : delta > 0
        ? "positions improved"
        : delta === 0
          ? "no change"
          : "positions lost";
  /**
   * AUT-355: `-strong` is the text/icon tier of the 3-tier status scale (`status-success-strong` /
   * `status-error-strong`), the only status tier meant to sit on a light surface as standalone
   * text. A test in `index.test.tsx` resolves whichever token this picks against
   * `tailwind.config.ts`'s `status` palette, so a typo'd or removed key fails rather than quietly
   * painting nothing.
   */
  const deltaColorClass =
    delta != null && delta > 0
      ? "text-status-success-strong"
      : delta != null && delta < 0
        ? "text-status-error-strong"
        : "";
  const DeltaIcon = delta != null && delta > 0 ? ArrowUpRight : delta != null && delta < 0 ? ArrowDownRight : null;
  const deltaValue = delta == null ? DASH : formatSignedInt(delta);

  const chartData = visibleDays.map((day, index) => {
    const row: Record<string, string | number | null> = {
      date: day,
      displayDate: formatCalendarDate(day),
      unitsSold: visibleUnitsSold[index] ?? null,
      bsr: visibleBsr[index] ?? null,
    };
    visibleLines.forEach((line) => {
      row[line.key] = line.positions[index] ?? null;
    });
    if (adSeriesActive) {
      const spend = plottedSpend[index];
      row.adSpend = spend;
      // Clicks/impressions/orders are not plotted (no axis) -- they ride the row for the tooltip,
      // and only for a day that carries spend data, so a missing (post-end-date) day shows none.
      row.adClicks = spend != null ? visibleAdClicks?.[index] ?? null : null;
      row.adImpressions = spend != null ? visibleAdImpressions?.[index] ?? null : null;
      row.adOrders = spend != null ? visibleAdOrders?.[index] ?? null : null;
    }
    return row;
  });

  const isProductLevel = target.level === "product";
  const isThemeLevel = target.level === "theme";
  const product = target.product;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* max-w-4xl: at 3xl a narrower dialog crowds the four stat tiles onto one line each -- the
          tiles then read as uneven. `Avg. keyword rank Δ (30D)` was the widest label and the one
          that wrapped at a narrower width; the shared `Keyword rank Δ (${d}D)` all three levels now
          use (`sources.md § 4.11`) is shorter and is no longer the widest. The dialog primitive
          already defaults to `bg-card`, so width is the only difference here. */}
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          {/* title+subtitle left, the range picker right with pr-8 so it clears the dialog's own
              close button. */}
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0 text-left">
              <DialogTitle>{stats.title}</DialogTitle>
              {/* At product level the subtitle belongs under the ASIN inside the inline block
                  below, not here -- `sources.md § 4.11`, Product call column, whose product block
                  is `56px image + ASIN link + subtitle`. The other two levels keep it in the
                  header, where their bordered card carries no subtitle of its own. */}
              {!isProductLevel && (
                <p className="text-sm text-muted-foreground">{stats.subtitle}</p>
              )}
            </div>
            <div className="shrink-0 pr-8">
              <KeywordModalWindowPicker
                selectedDays={selectedDays}
                onChange={setSelectedDays}
                maxAvailableDays={maxAvailableDays}
              />
            </div>
          </div>
        </DialogHeader>

        {isProductLevel ? (
          // `sources.md § 4.11`, Product call column: inline 56px image + ASIN link + subtitle,
          // with no repeated product name -- the `DialogTitle` above already reads `product.name`,
          // so the bordered card the other two levels use would just repeat it. The subtitle sits
          // here rather than in the header because this is the block the capture attaches it to.
          <div className="flex items-center gap-3">
            {product.imageUrl ? (
              <img
                src={product.imageUrl}
                alt=""
                className="h-14 w-14 shrink-0 rounded object-contain border border-border bg-card"
              />
            ) : (
              // `ProductRow.tsx`'s own null-imageUrl placeholder, so the two agree on this screen.
              <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded border border-border bg-card text-muted-foreground">
                <Package className="h-4 w-4" />
              </span>
            )}
            <div className="min-w-0">
              <a
                href={`https://www.amazon.com/dp/${product.asin}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[11px] leading-tight text-primary hover:underline"
              >
                {product.asin}
              </a>
              <p className="mt-1 text-sm text-muted-foreground">{stats.subtitle}</p>
            </div>
          </div>
        ) : (
          // Photo + name + ASIN. In the design this is `ProductCardSmall`, a shared design-system
          // component used in five places there -- but this codebase has no equivalent (`shared/
          // ProductCard.tsx` exports only `ProductCardLarge`), so this is inline and specific to
          // this modal. Styled to match that component rather than approximating it: the rounded
          // bordered surface is our own `Card` (`variant="default"` is `rounded-sm border`) and the
          // name is `font-semibold`. Keyword and theme levels only -- the product level's own
          // inline treatment above replaces this (it would otherwise repeat the `DialogTitle`).
          // `min-w-0`: `DialogContent` is a single-column grid, and an `auto` track never shrinks
          // below its items' minimum contribution. `TruncatedText` renders the name `truncate`
          // (`whitespace-nowrap`), so this card's min-content is `56px image + the name's full
          // unwrapped width` -- 889px for a real Amazon title. That widened the track past the
          // dialog's 896px - p-6 content box, pushing this card and the `Kw. sales` tile out the
          // right edge and sliding the range picker under the dialog's own close button. The inner
          // `min-w-0` below only removes the flex item's auto minimum; the grid item needs its own.
          <Card className="min-w-0 flex items-center gap-3 p-2.5">
            {product.imageUrl ? (
              <img
                src={product.imageUrl}
                alt=""
                className="h-14 w-14 shrink-0 rounded object-contain border border-border bg-card"
              />
            ) : (
              <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded border border-border bg-card text-muted-foreground">
                <Package className="h-4 w-4" />
              </span>
            )}
            <div className="min-w-0">
              <TruncatedText className="text-sm font-semibold leading-tight text-foreground">
                {product.name}
              </TruncatedText>
              <a
                href={`https://www.amazon.com/dp/${product.asin}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[11px] leading-tight text-primary hover:underline"
              >
                {product.asin}
              </a>
            </div>
          </Card>
        )}

        {/* AUT-440: the Ad Spend card is the fifth and rightmost tile, at every level whose row has
            ad rows (`adSeriesActive`). Its presence widens the row from four to five columns at
            `sm`; when it is absent the grid collapses to four, exactly as the capture does for its
            own no-spend state (FR-20). */}
        <div
          className={cn(
            "grid grid-cols-2 gap-4 py-4",
            adSeriesActive ? "sm:grid-cols-5" : "sm:grid-cols-4",
          )}
        >
          <StatTile
            label={`${stats.deltaLabel} (${visibleDays.length}D)`}
            valueClassName={deltaColorClass}
            value={
              <>
                <span>{deltaValue}</span>
                {DeltaIcon && <DeltaIcon className="h-5 w-5" />}
              </>
            }
            caption={deltaCaption}
          />
          <StatTile label="Search volume" value={stats.tile2.value} caption={stats.tile2.caption} />
          <StatTile
            label="KPS"
            value={
              <>
                <span>{stats.tile3.value}</span>
                <KpsBreakdownPopover viewModel={stats.tile3.kpsViewModel} />
              </>
            }
            caption={stats.tile3.caption}
          />
          <StatTile label="Kw. sales" value={stats.tile4.value} caption={stats.tile4.caption} />
          {adSeriesActive && (
            <AdSpendCard
              label={adLabel}
              total={adWindowTotal}
              hasWindowSpend={adWindowHasSpend}
              dayCount={visibleDays.length}
              maxAsinsInAdGroup={resolved.maxAsinsInAdGroup ?? 1}
            />
          )}
        </div>

        <div className="h-[300px] pt-4">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid vertical={false} stroke="hsl(var(--border))" />
              <XAxis
                dataKey="displayDate"
                tick={{ fontSize: 11 }}
                className="fill-muted-foreground"
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                yAxisId="rank"
                orientation="left"
                reversed
                domain={[1, "auto"]}
                width={44}
                tick={{ fontSize: 10 }}
                className="fill-muted-foreground"
                axisLine={false}
                tickLine={false}
                label={{
                  value: "Keyword Rank",
                  angle: -90,
                  position: "insideLeft",
                  style: { fill: "hsl(var(--muted-foreground))", fontSize: 11, textAnchor: "middle" },
                }}
              />
              {/* The Units Sold axis draws at the keyword and product levels (roll-up-ad-spend FR-10),
                  not at the theme level (AUT-626): units sold is a product number and would only
                  repeat on every theme. BSR still draws only below the product level. */}
              {!isThemeLevel && (
                <YAxis
                  yAxisId="units"
                  orientation="right"
                  domain={[0, "auto"]}
                  width={48}
                  tick={{ fontSize: 10 }}
                  className="fill-muted-foreground"
                  axisLine={false}
                  tickLine={false}
                  label={{
                    value: "Units Sold",
                    angle: 90,
                    position: "insideRight",
                    style: { fill: "hsl(var(--muted-foreground))", fontSize: 11, textAnchor: "middle" },
                  }}
                />
              )}
              {/* BSR's scale is a different magnitude from rank; it draws against its own axis, which
                  carries no visible label of its own — the design names only the rank and units-sold axes.
                  Product level renders no BSR line (below), so this axis is skipped there. */}
              {!isProductLevel && (
                <YAxis yAxisId="bsr" orientation="left" reversed domain={["auto", "auto"]} hide />
              )}
              {/* AUT-440: the spend series' own right-hand `$` axis (FR-17), outboard of Units Sold
                  since recharts stacks right-orientation axes in JSX order. Drawn only with the
                  series (gate 1) so no axis ever appears without a line. `$`-formatted ticks, title
                  case `Ad Spend` label as the capture names it. */}
              {adSeriesActive && (
                <YAxis
                  yAxisId="spend"
                  orientation="right"
                  domain={[0, "auto"]}
                  width={56}
                  tick={{ fontSize: 10 }}
                  className="fill-muted-foreground"
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(value: number) => `$${Number(value).toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
                  label={{
                    value: "Ad Spend",
                    angle: 90,
                    position: "insideRight",
                    style: { fill: "hsl(var(--muted-foreground))", fontSize: 11, textAnchor: "middle" },
                  }}
                />
              )}
              {/* AUT-440: a custom tooltip so clicks/impressions/orders (not plotted, so no axis) and
                  the shared-spend disclosure can join the plotted series (FR-18/FR-19), at all three
                  levels. It receives the resolved row's `maxAsinsInAdGroup`. */}
              <RechartsTooltip content={<ChartTooltip maxAsinsInAdGroup={resolved.maxAsinsInAdGroup} />} />
              <Legend wrapperStyle={{ fontSize: "12px", paddingTop: "8px" }} />
              {/* The Units Sold bar draws at the keyword and product levels (roll-up-ad-spend FR-10),
                  not at the theme level (AUT-626) -- which also drops its legend entry and tooltip row. */}
              {!isThemeLevel && (
                <Bar
                  yAxisId="units"
                  dataKey="unitsSold"
                  name="Units Sold"
                  fill="hsl(var(--primary) / 0.2)"
                  radius={[2, 2, 0, 0]}
                  barSize={14}
                />
              )}
              {/* FR-13: one Line per theme at the product level (dataKey `theme{i}`, positional --
                  a theme name is not a safe object key and `themeId` is null for one of them), the
                  seven-stop `--chart-1..7` ramp (AUT-355: this is the merged brand/KPS ramp that
                  used to live at `--kps-1..7`; `--chart-8`/`--chart-9` are reserved for other
                  multi-series charts and are not part of this rotation). The second cycle (theme
                  index >= 7) is dashed so theme 8 is never visually theme 1. At the keyword and
                  theme levels this renders exactly the single `Keyword Rank` line that shipped,
                  keyed `rank`. */}
              {visibleLines.map((line, index) => {
                const kpsStop = (index % 7) + 1;
                const stroke = isProductLevel ? `hsl(var(--chart-${kpsStop}))` : "hsl(var(--primary))";
                const secondCycle = isProductLevel && index >= 7;
                return (
                  <Line
                    key={line.key}
                    yAxisId="rank"
                    type="monotone"
                    dataKey={line.key}
                    name={line.name}
                    stroke={stroke}
                    strokeWidth={2.5}
                    strokeDasharray={secondCycle ? "4 3" : undefined}
                    // recharts' default `legendType` for `Line` is `'line'`, whose legend icon is a
                    // fixed loop that ignores `strokeDasharray` -- only `'plainline'` reads
                    // `data.payload.strokeDasharray` onto the icon (recharts/es6/component/
                    // DefaultLegendContent.js renderIcon). At the product level this is what makes
                    // the second-cycle (dashed) theme legend swatches carry the same dash as their
                    // line, so theme 8 is never indistinguishable from theme 1. Scoped to the
                    // product level only -- the single keyword/theme-level line is never dashed and
                    // keeps the icon it already shipped with.
                    legendType={isProductLevel ? "plainline" : undefined}
                    // Filled dots on the single-line views -- they carry meaning specifically
                    // there: `connectNulls={false}` breaks the line at a day with no reading, so
                    // the dots are what show which days actually had one. The product view's many
                    // lines skip dots to stay legible; BSR keeps `dot={false}` at every level.
                    // AUT-380: the one exception at the product level is a day whose reading is
                    // isolated (both neighbours empty) -- with no dot that day is a degenerate
                    // subpath SVG paints as nothing, so the reading is invisible even on hover
                    // short of landing the pointer exactly on it. A function `dot` lets each of
                    // the many theme lines answer per-point, from its own `positions`, rather than
                    // turning dots on for the whole line (which would repaint every other day too).
                    dot={
                      isProductLevel
                        ? (dotProps: LineDotRenderProps) =>
                            dotProps.index != null && isIsolatedReading(line.positions, dotProps.index) ? (
                              <Dot
                                key={`isolated-dot-${line.key}-${dotProps.index}`}
                                cx={dotProps.cx}
                                cy={dotProps.cy}
                                r={3}
                                fill={stroke}
                              />
                            ) : null
                        : { r: 3, fill: stroke }
                    }
                    activeDot={{ r: isProductLevel ? 3 : 4 }}
                    connectNulls={false}
                  />
                );
              })}
              {/* BSR is still omitted at the product level -- `bsr` flows through `chartData`
                  unchanged, only this rendering is skipped. */}
              {!isProductLevel && (
                <Line
                  yAxisId="bsr"
                  type="monotone"
                  dataKey="bsr"
                  name="BSR"
                  stroke="hsl(var(--chart-5))"
                  strokeWidth={1.5}
                  strokeDasharray="5 3"
                  dot={false}
                  activeDot={{ r: 3 }}
                  connectNulls={false}
                />
              )}
              {/* AUT-440: the Daily Ad Spend series on its own `$` axis, after rank/BSR so it paints
                  under them. An `<Area>` with a 12%-opacity fill as the capture drew it, but two
                  divergences resolve toward the ticket (`components/rank-tracker.md § The screen`):
                  the resting stroke is **solid**, not the prototype's always-dashed `4 3`; and
                  `connectNulls={false}` reuses this chart's existing missing-data style -- the break
                  the rank and BSR lines already use for a gap. A `0` day plots at the baseline (a
                  fact); a day past `adSpendEndDate` was nulled into `chartData` (`plottedSpend`), so
                  the area breaks there and the post-end-date tail reads as missing, never as `$0`
                  (FR-17). */}
              {adSeriesActive && (
                <Area
                  yAxisId="spend"
                  type="monotone"
                  dataKey="adSpend"
                  name={adSeriesName}
                  stroke="hsl(var(--heat-sp-3))"
                  strokeWidth={1.5}
                  fill="hsl(var(--heat-sp-4) / 0.25)"
                  dot={false}
                  activeDot={{ r: 3 }}
                  legendType="plainline"
                  connectNulls={false}
                />
              )}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </DialogContent>
    </Dialog>
  );
}
